import {Hero, Text, InlineLink, Grid} from '@primer/react-brand-next'
import type {ComponentProps} from 'react'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS, INLINES, MARKS, type Document} from '@contentful/rich-text-types'
import {documentToPlainTextString, getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'
import styles from '../FlexSuite.module.css'

const getDescription = (description: Document) => {
  return documentToReactComponents(description, {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => <Text>{children}</Text>,
      [INLINES.HYPERLINK]: (node, children) => {
        return (
          <InlineLink
            href={node.data.uri}
            {...getAnalyticsEvent({
              action: documentToPlainTextString(node, ' '),
              tag: 'hyperlink',
              location: 'Flex Suite Hero Description',
            })}
          >
            {children}
          </InlineLink>
        )
      },
    },
    renderMark: {
      [MARKS.BOLD]: text => (
        <Text as="span" variant="default">
          {text}
        </Text>
      ),
    },
  })
}

const getHeading = (heading: Document) => {
  return documentToReactComponents(heading, {
    renderMark: {
      [MARKS.BOLD]: text => <span className={styles.heroHeading}>{text}</span>,
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => <>{children}</>,
    },
  })
}

type FlexSuiteHeroProps = {
  heroHeading: Document
  heroLabel?: string
  heroDescription?: Document
  heroCtaOne: {fields: {href: string; text: string}}
  heroCtaTwo?: {fields: {href: string; text: string}}
  heroTertiaryDescription?: Document
  options?: {
    variant?: ComponentProps<typeof Hero>['variant']
    enableAnimation?: boolean
  }
}

export function FlexSuiteHero(fields: FlexSuiteHeroProps) {
  const {heroHeading, heroLabel, heroDescription, heroCtaOne, heroCtaTwo, heroTertiaryDescription, options} = fields
  return (
    <Grid>
      <Grid.Column>
        <Hero
          variant={options?.variant}
          enableAnimation={options?.enableAnimation}
          trailingComponent={() => (heroTertiaryDescription ? getDescription(heroTertiaryDescription) : null)}
        >
          {heroLabel && <Hero.Label>{heroLabel}</Hero.Label>}
          <Hero.Heading>{getHeading(heroHeading)}</Hero.Heading>
          <Hero.Description>{heroDescription ? getDescription(heroDescription) : null}</Hero.Description>
          <Hero.PrimaryAction href={heroCtaOne.fields.href}>{heroCtaOne.fields.text}</Hero.PrimaryAction>
          {heroCtaTwo && (
            <Hero.SecondaryAction href={heroCtaTwo.fields.href}>{heroCtaTwo.fields.text}</Hero.SecondaryAction>
          )}
        </Hero>
      </Grid.Column>
    </Grid>
  )
}
